import React, { useState, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { createAxiosInstance } from '../../utils/Axios';

const ImportLeadsModal = ({ isOpen, onClose, onImportSuccess, columnNames = {}, customFields = [] }) => {
  const fileInputRef = useRef(null);

  // Use useMemo to recalculate CRM_FIELDS when columnNames changes
  const CRM_FIELDS = useMemo(() => {
    // Define standard field mapping
    const standardFields = [
      { key: 'name', displayKey: 'leadName', fallback: 'Lead Name' },
      { key: 'email', displayKey: 'email', fallback: 'Email' },
      { key: 'phone', displayKey: 'phone', fallback: 'Phone Number' },
      { key: 'source', displayKey: 'source', fallback: 'Source' },
      { key: 'sourceId', displayKey: 'sourceId', fallback: 'Source ID' },
      { key: 'address', displayKey: 'address', fallback: 'Address' },
      { key: 'organization', displayKey: 'organization', fallback: 'Organization' },
      { key: 'leadStatus', displayKey: 'status', fallback: 'Lead Status' },
      { key: 'paymentStatus', displayKey: 'payment', fallback: 'Payment Status' },
      { key: 'remarks', displayKey: 'remarks', fallback: 'Remarks' },
      { key: 'createdTime', displayKey: 'createdTime', fallback: 'Create Time / Created Time' },
      { key: 'campaignId', displayKey: 'campaignId', fallback: 'Campaign ID' },
      { key: 'nextAction', displayKey: 'nextAction', fallback: 'Next Action' },
      { key: 'nextActionDueDate', displayKey: 'nextActionDueDate', fallback: 'Next Action Due Date' },
      { key: 'budget', displayKey: 'budget', fallback: 'Budget' },
      { key: 'probability', displayKey: 'probability', fallback: 'Probability' },
      { key: 'assignedTo', displayKey: 'assignedTo', fallback: 'Assigned To' }
    ];

    // Build fields from standard mapping with columnNames
    return standardFields.map(({ key, displayKey, fallback }) => ({
      key,
      label: columnNames[displayKey] || fallback
    }));
  }, [columnNames]);

  // Step: 'upload' | 'map' | 'importing'
  const [step, setStep] = useState('upload');

  // Raw data from Excel: array of arrays
  const [excelColumns, setExcelColumns] = useState([]);
  const [excelRows, setExcelRows] = useState([]);
  const [fileName, setFileName] = useState('');

  // Mapping: { excelColName -> crmFieldKey | '' }
  const [mapping, setMapping] = useState({});

  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        if (!rawData || rawData.length < 2) {
          toast.error('Excel file is empty or has no data rows.');
          return;
        }

        const headers = rawData[0].map(h => String(h).trim());
        const dataRows = rawData.slice(1).filter(row =>
          row.some(cell => cell !== '' && cell !== null && cell !== undefined)
        );

        const rowObjects = dataRows.map(row => {
          const obj = {};
          headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
          return obj;
        });

        setExcelColumns(headers);
        setExcelRows(rowObjects);

        // Auto-map: match excel column names to CRM fields or custom/dynamic fields
        const autoMap = {};
        const usedFields = new Set(); // Track used fields to prevent duplicates
        
        headers.forEach(col => {
          const normalizedCol = col.toLowerCase().replace(/[\s_-]/g, '');
          let matchedKey = '';
          
          // Helper dictionary for standard field aliases
          const aliases = {
            name: ['fullname', 'leadname', 'customername', 'name'],
            email: ['email', 'emailid', 'mail'],
            phone: ['phone', 'phonenumber', 'phoneno', 'mobile', 'contact'],
            source: ['source', 'platform'],
            sourceId: ['sourceid'],
            leadStatus: ['leadstatus', 'status', 'isqualified', 'qualified'],
            paymentStatus: ['paymentstatus', 'payment'],
            remarks: ['remarks', 'notes', 'feedback', 'comment', 'comments'],
            createdTime: ['createdtime', 'createtime', 'submitteddate', 'createdat', 'time'],
            campaignId: ['campaignid', 'campaign', 'campaignname'],
            address: ['address', 'city', 'location'],
            organization: ['organization', 'company'],
            budget: ['budget'],
            probability: ['probability']
          };

          // 1. Try standard fields
          for (const f of CRM_FIELDS) {
            const fl = f.label.toLowerCase().replace(/[\s_-]/g, '');
            const fk = f.key.toLowerCase().replace(/[\s_-]/g, '');
            const fieldAliases = aliases[f.key] || [fk, fl];

            if (fieldAliases.some(alias => alias === normalizedCol || normalizedCol.includes(alias) || alias.includes(normalizedCol)) &&
                !usedFields.has(f.key)) {
              matchedKey = f.key;
              break;
            }
          }

          // 2. Try existing custom fields
          if (!matchedKey) {
            for (const f of customFields) {
              const fl = (f.fieldName || '').toLowerCase().replace(/[\s_-]/g, '');
              const fk = (f.fieldKey || '').toLowerCase().replace(/[\s_-]/g, '');
              const customKey = `custom_${f.fieldKey}`;

              if ((fl === normalizedCol || fk === normalizedCol) && !usedFields.has(customKey)) {
                matchedKey = customKey;
                break;
              }
            }
          }

          // 3. Fallback to dynamic custom field matching the column name
          if (!matchedKey) {
            const safeKey = col.toLowerCase().replace(/[\s_-]+/g, '_').replace(/[^a-z0-9_]/g, '');
            const customKey = `custom_${safeKey}`;
            if (!usedFields.has(customKey)) {
              matchedKey = customKey;
            }
          }
          
          if (matchedKey) {
            autoMap[col] = matchedKey;
            usedFields.add(matchedKey);
          } else {
            autoMap[col] = '';
          }
        });
        
        setMapping(autoMap);
        setStep('map');
        toast.success(`Loaded ${rowObjects.length} rows from "${sheetName}"`);
      } catch (err) {
        console.error(err);
        toast.error('Failed to read Excel file. Please use .xlsx or .csv format.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (excelCol, crmKey) => {
    // If we're unselecting a field (choosing empty), just update
    if (!crmKey) {
      setMapping(prev => ({ ...prev, [excelCol]: '' }));
      return;
    }

    // Check if this field is already used by another column
    const isFieldUsed = Object.entries(mapping).some(([col, key]) => 
      key === crmKey && col !== excelCol
    );

    if (isFieldUsed) {
      toast.warning('This field is already mapped to another column. Please select a different field.');
      return;
    }

    setMapping(prev => ({ ...prev, [excelCol]: crmKey }));
  };

  // Helper function to get available fields for a specific column
  const getAvailableFields = (currentColumn) => {
    const usedFields = new Set();
    
    // Collect all fields that are already mapped to other columns
    Object.entries(mapping).forEach(([col, key]) => {
      if (col !== currentColumn && key) {
        usedFields.add(key);
      }
    });

    // Build the available fields list
    const availableFields = [];
    const addedKeys = new Set();
    
    // Add standard CRM fields
    CRM_FIELDS.forEach(f => {
      availableFields.push({
        key: f.key,
        label: f.label,
        isUsed: usedFields.has(f.key)
      });
      addedKeys.add(f.key);
    });

    // Add existing custom fields
    customFields.forEach(f => {
      const customKey = `custom_${f.fieldKey}`;
      availableFields.push({
        key: customKey,
        label: `${(f.fieldName || f.fieldKey).toUpperCase()} (Custom)`,
        isUsed: usedFields.has(customKey)
      });
      addedKeys.add(customKey);
    });

    // Add dynamic fields for all Excel columns detected in the uploaded file
    excelColumns.forEach(col => {
      const safeKey = col.toLowerCase().replace(/[\s_-]+/g, '_').replace(/[^a-z0-9_]/g, '');
      const customKey = `custom_${safeKey}`;
      if (!addedKeys.has(customKey)) {
        availableFields.push({
          key: customKey,
          label: `${col.toUpperCase()} (Dynamic Field)`,
          isUsed: usedFields.has(customKey)
        });
        addedKeys.add(customKey);
      }
    });

    return availableFields;
  };

  const handleImport = async () => {
    const hasMappedField = Object.values(mapping).some(v => v !== '');
    if (!hasMappedField) {
      toast.error('Please map at least one column before importing.');
      return;
    }

    setIsImporting(true);
    const axiosInstance = createAxiosInstance();

    // Auto-create missing custom field definitions for any mapped custom keys
    const customKeysToCreate = new Map();
    Object.entries(mapping).forEach(([excelCol, crmKey]) => {
      if (crmKey && crmKey.startsWith('custom_')) {
        const fieldKey = crmKey.replace('custom_', '');
        const existsInCustomFields = customFields.some(f => f.fieldKey === fieldKey);
        if (!existsInCustomFields) {
          customKeysToCreate.set(fieldKey, excelCol);
        }
      }
    });

    if (customKeysToCreate.size > 0) {
      for (const [fieldKey, fieldName] of customKeysToCreate.entries()) {
        try {
          await axiosInstance.post('/leads/field', {
            fieldName: fieldName,
            fieldKey: fieldKey,
            fieldType: 'text'
          });
        } catch (e) {
          console.warn('Could not auto-create custom field definition:', fieldKey, e);
        }
      }
    }

    let successCount = 0;
    let failCount = 0;
    const importedLeads = [];

    for (const row of excelRows) {
      try {
        const formData = {};
        const customFieldsData = {};
        let source = 'Import';
        let sourceId = '';
        let leadStatus = 'New';
        let paymentStatus = 'Pending';
        let remarks = '';
        let createdTime = null;
        let nextActionDueDate = null;
        let nextAction = null;
        let campaignId = null;

        Object.entries(mapping).forEach(([excelCol, crmKey]) => {
          if (!crmKey) return;
          const val = String(row[excelCol] ?? '').trim();

          // Standard fields
          if (crmKey === 'name') formData.name = val;
          else if (crmKey === 'email') formData.email = val;
          else if (crmKey === 'phone') formData.phone = val;
          else if (crmKey === 'address') formData.address = val;
          else if (crmKey === 'organization') formData.organization = val;
          else if (crmKey === 'source') source = val || 'Import';
          else if (crmKey === 'sourceId') sourceId = val;
          else if (crmKey === 'leadStatus') leadStatus = val || 'New';
          else if (crmKey === 'paymentStatus') paymentStatus = val || 'Pending';
          else if (crmKey === 'remarks') remarks = val;
          else if (crmKey === 'createdTime') {
            if (val) createdTime = val;
          }
          else if (crmKey === 'nextAction') {
            if (val) nextAction = val;
          }
          else if (crmKey === 'nextActionDueDate') {
            if (val) nextActionDueDate = val;
          }
          else if (crmKey === 'campaignId') {
            if (val) {
              if (/^[0-9a-fA-F]{24}$/.test(val)) {
                campaignId = val;
              } else {
                customFieldsData['campaign_id'] = val;
              }
            }
          }
          else if (crmKey === 'budget') {
            if (val) formData.budget = val;
          }
          else if (crmKey.startsWith('custom_')) {
            const fieldKey = crmKey.replace('custom_', '');
            customFieldsData[fieldKey] = val;
          }
          else if (crmKey) {
            // Check if custom field
            const isCustomField = customFields.some(f => 
              f.fieldKey === crmKey || 
              f.fieldKey === crmKey.replace('custom_', '')
            );
            if (isCustomField) {
              const fieldKey = crmKey.replace('custom_', '');
              customFieldsData[fieldKey] = val;
            } else {
              formData[crmKey] = val;
            }
          }
        });

        if (remarks) formData.remarks = remarks;

        // Build the request body
        const requestBody = {
          source: source || 'Import',
          sourceId,
          formData,
          leadStatus: row['Status'] || row['status'] || (row['is_qualified'] === 'YES' || row['is_qualified'] === 'true' ? 'Qualified' : leadStatus),
          paymentStatus,
          remarks: remarks || formData.remarks || row['Remarks'] || row['Feedback'] || '',
          createdTime: createdTime || row['created_time'] || row['Create Time'] || null,
          nextAction,
          nextActionDueDate,
          campaignId,
          customFields: customFieldsData
        };

        const res = await axiosInstance.post('/leads', requestBody);

        if (res.data && res.data.success) {
          importedLeads.push(res.data.lead);
          successCount++;
        } else {
          console.error('Lead import failed:', res.data?.message);
          failCount++;
        }
      } catch (err) {
        console.error('Failed to import row:', err.response?.data?.message || err.message || err);
        failCount++;
      }
    }

    setIsImporting(false);
    setImportResult({ success: successCount, failed: failCount });
    setStep('result');
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} lead(s)!`);
      onImportSuccess && onImportSuccess(importedLeads);
    }
    if (failCount > 0) {
      toast.warning(`${failCount} row(s) failed to import.`);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setExcelColumns([]);
    setExcelRows([]);
    setFileName('');
    setMapping({});
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.55)',
      zIndex: 1050,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        backgroundColor: '#fff',
        width: '92%',
        maxWidth: '920px',
        maxHeight: '92vh',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 32px 64px rgba(0,0,0,0.22)',
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid #F3F4F6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#fff' }}>Import Leads from Excel</h2>
              <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
                Upload your Excel file and map columns to CRM fields
              </p>
            </div>
          </div>
          <button onClick={handleClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none',
            borderRadius: '8px', cursor: 'pointer',
            width: '32px', height: '32px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '18px', fontWeight: '300',
            transition: 'background 0.2s',
          }}>×</button>
        </div>

        {/* ── Step indicator ── */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 28px',
          borderBottom: '1px solid #F3F4F6', gap: '0',
          backgroundColor: '#FAFAFA',
        }}>
          {[
            { id: 'upload', label: '1  Upload File' },
            { id: 'map', label: '2  Map Columns' },
            { id: 'done', label: '3  Import' },
          ].map((s, idx) => {
            const isActive = step === s.id || (step === 'importing' && s.id === 'done');
            const isDone = (idx === 0 && (step === 'map' || step === 'importing' || importResult)) ||
              (idx === 1 && (step === 'importing' || importResult));
            return (
              <React.Fragment key={s.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700',
                    backgroundColor: isDone ? '#10B981' : isActive ? '#7C3AED' : '#E5E7EB',
                    color: isDone || isActive ? '#fff' : '#6B7280',
                    transition: 'all 0.3s',
                  }}>
                    {isDone ? '✓' : idx + 1}
                  </div>
                  <span style={{
                    fontSize: '13px', fontWeight: isActive ? '700' : '500',
                    color: isActive ? '#7C3AED' : isDone ? '#10B981' : '#9CA3AF',
                  }}>{s.label}</span>
                </div>
                {idx < 2 && (
                  <div style={{
                    flex: 1, height: '2px', margin: '0 12px',
                    backgroundColor: isDone ? '#10B981' : '#E5E7EB',
                    transition: 'background 0.3s',
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '24px 28px', position: 'relative' }}>

          {/* STEP 1: Upload */}
          {step === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', gap: '20px' }}>
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2.5px dashed #C4B5FD',
                  borderRadius: '16px',
                  padding: '48px 64px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#FAF5FF',
                  transition: 'all 0.2s',
                  width: '100%',
                  maxWidth: '480px',
                  boxSizing: 'border-box',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#7C3AED'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#C4B5FD'}
              >
                <div style={{
                  width: '64px', height: '64px', borderRadius: '16px',
                  backgroundColor: '#EDE9FE', margin: '0 auto 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#1F2937' }}>
                  Click to Upload Excel File
                </p>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6B7280' }}>
                  Supports .xlsx and .csv formats
                </p>
                <div style={{
                  display: 'inline-block', padding: '8px 24px',
                  backgroundColor: '#7C3AED', color: '#fff',
                  borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                }}>Browse File</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <p style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', margin: 0 }}>
                The first row of your Excel file will be treated as column headers.
              </p>
            </div>
          )}

          {/* STEP 2: Map Columns */}
          {step === 'map' && !importResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* File info */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 16px', backgroundColor: '#F0FDF4',
                borderRadius: '10px', border: '1px solid #BBF7D0',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '8px',
                  backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#065F46' }}>{fileName}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#059669' }}>
                    {excelRows.length} data rows · {excelColumns.length} columns detected
                  </p>
                </div>
                <button onClick={handleReset} style={{
                  marginLeft: 'auto', padding: '6px 12px',
                  border: '1px solid #D1FAE5', borderRadius: '6px',
                  backgroundColor: '#fff', color: '#059669',
                  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                }}>
                  Change File
                </button>
              </div>

              {/* Mapping area */}
              <div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 40px minmax(0, 1fr)',
                  gap: '0', padding: '10px 16px',
                  backgroundColor: '#F9FAFB', borderRadius: '8px 8px 0 0',
                  border: '1px solid #E5E7EB', borderBottom: '2px solid #E5E7EB',
                  boxSizing: 'border-box',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Excel Column
                  </span>
                  <span />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    CRM Field
                  </span>
                </div>
                <div style={{ border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 8px 8px', overflowY: 'auto', maxHeight: '340px' }}>
                  {excelColumns.map((col, i) => {
                    const availableFields = getAvailableFields(col);
                    const currentValue = mapping[col] || '';
                    
                    return (
                      <div key={col} style={{
                        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 40px minmax(0, 1fr)',
                        alignItems: 'center', padding: '10px 16px',
                        borderBottom: i < excelColumns.length - 1 ? '1px solid #F3F4F6' : 'none',
                        backgroundColor: i % 2 === 0 ? '#fff' : '#FAFAFA',
                        transition: 'background 0.15s',
                        boxSizing: 'border-box',
                      }}>
                        {/* Excel column name */}
                        <div style={{
                          padding: '8px 12px', backgroundColor: '#EDE9FE',
                          borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                          color: '#5B21B6', border: '1px solid #DDD6FE',
                          display: 'flex', alignItems: 'center', gap: '8px',
                          wordBreak: 'break-word', overflowWrap: 'anywhere',
                          minWidth: 0, overflow: 'hidden',
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <line x1="3" y1="9" x2="21" y2="9" />
                            <line x1="3" y1="15" x2="21" y2="15" />
                            <line x1="9" y1="3" x2="9" y2="21" />
                          </svg>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }}>
                            {col}
                          </span>
                        </div>

                        {/* Arrow */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke={currentValue ? '#10B981' : '#D1D5DB'} strokeWidth="2.5">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </div>

                        {/* CRM field dropdown */}
                        <select
                          value={currentValue}
                          onChange={e => handleMappingChange(col, e.target.value)}
                          style={{
                            padding: '8px 12px', borderRadius: '6px',
                            border: currentValue ? '1px solid #A7F3D0' : '1px solid #E5E7EB',
                            fontSize: '13px', color: currentValue ? '#065F46' : '#9CA3AF',
                            backgroundColor: currentValue ? '#F0FDF4' : '#fff',
                            outline: 'none', cursor: 'pointer', fontWeight: currentValue ? '600' : '400',
                            transition: 'all 0.2s',
                            width: '100%', minWidth: 0, boxSizing: 'border-box',
                          }}
                        >
                          <option value="">— Skip this column —</option>
                          {availableFields.map(f => (
                            <option 
                              key={f.key} 
                              value={f.key}
                              disabled={f.isUsed}
                              style={{
                                color: f.isUsed ? '#9CA3AF' : '#1F2937',
                                backgroundColor: f.isUsed ? '#F3F4F6' : '#fff'
                              }}
                            >
                              {f.label} {f.isUsed ? '(Already mapped)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Preview table */}
              {excelRows.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                    Preview (first 5 rows)
                  </h4>
                  <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                          {excelColumns.map(col => (
                            <th key={col} style={{
                              padding: '8px 12px', textAlign: 'left',
                              fontWeight: '700', color: '#374151',
                              whiteSpace: 'nowrap',
                            }}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelRows.slice(0, 5).map((row, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            {excelColumns.map(col => (
                              <td key={col} style={{
                                padding: '8px 12px', color: '#4B5563',
                                whiteSpace: 'nowrap', maxWidth: '180px',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {String(row[col] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {excelRows.length > 5 && (
                    <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                      ... and {excelRows.length - 5} more rows
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Result */}
          {importResult && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', minHeight: '280px', gap: '16px', textAlign: 'center',
            }}>
              <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                backgroundColor: importResult.success > 0 ? '#D1FAE5' : '#FEE2E2',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '32px',
              }}>
                {importResult.success > 0 ? '✅' : '❌'}
              </div>
              <div>
                <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '800', color: '#111827' }}>
                  Import Complete!
                </h3>
                <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
                  <div style={{
                    padding: '12px 24px', backgroundColor: '#F0FDF4', borderRadius: '10px',
                    border: '1px solid #BBF7D0',
                  }}>
                    <p style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: '800', color: '#059669' }}>
                      {importResult.success}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#065F46', fontWeight: '600' }}>Imported</p>
                  </div>
                  {importResult.failed > 0 && (
                    <div style={{
                      padding: '12px 24px', backgroundColor: '#FFF7ED', borderRadius: '10px',
                      border: '1px solid #FED7AA',
                    }}>
                      <p style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: '800', color: '#D97706' }}>
                        {importResult.failed}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#92400E', fontWeight: '600' }}>Failed</p>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={handleClose} style={{
                padding: '10px 32px', backgroundColor: '#7C3AED', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '14px',
                fontWeight: '700', cursor: 'pointer', marginTop: '8px',
              }}>
                Done
              </button>
            </div>
          )}

          {/* Importing spinner overlay */}
          {isImporting && (
            <div style={{
              position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.85)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: '16px', gap: '16px',
            }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                border: '4px solid #EDE9FE', borderTopColor: '#7C3AED',
                animation: 'spin 0.8s linear infinite',
              }} />
              <p style={{ fontSize: '15px', fontWeight: '700', color: '#5B21B6', margin: 0 }}>
                Importing leads…
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {!importResult && (
          <div style={{
            padding: '16px 28px',
            borderTop: '1px solid #F3F4F6',
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px',
            backgroundColor: '#FAFAFA',
          }}>
            <button onClick={handleClose} style={{
              padding: '9px 22px', backgroundColor: '#fff',
              border: '1px solid #E5E7EB', borderRadius: '8px',
              color: '#374151', fontWeight: '600', fontSize: '13px', cursor: 'pointer',
            }}>
              Cancel
            </button>
            {step === 'map' && (
              <button
                onClick={handleImport}
                disabled={isImporting}
                style={{
                  padding: '9px 28px', backgroundColor: '#7C3AED',
                  border: 'none', borderRadius: '8px',
                  color: '#fff', fontWeight: '700', fontSize: '13px',
                  cursor: isImporting ? 'not-allowed' : 'pointer',
                  opacity: isImporting ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  boxShadow: '0 4px 12px rgba(124,58,237,0.35)',
                  transition: 'all 0.2s',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Import {excelRows.length} Lead{excelRows.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportLeadsModal;